<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4 mb-2">
      <d-col
        cols="12"
        lg="4"
        class="text-center text-sm-left mb-4 mb-md-0">
        <span class="text-uppercase page-subtitle">Cenniki</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja cennika
            <p
              class="my-2"
              style="font-weight: 100;">{{ data.name }}</p>
          </template>
          <template v-else>Dodawanie cennika</template>
        </h3>
      </d-col>
      <d-col
        cols="12"
        lg="8"
        class="text-right text-sm-right d-flex justify-content-end align-content-center flex-wrap">
        <div
          class="w-100 d-flex justify-content-end"
          style="gap: 16px;">
          <d-button
            theme="secondary"
            @click.prevent="$router.push('/cenniki')">
            <i class="material-icons">arrow_back</i>
            Wróć do listy
          </d-button>
          <d-button
            theme="success"
            :disabled="!allDataLoaded || load_error"
            :class="{ 'isLoading' : form_submitting }"
            @click.prevent="validateForm">
            Zapisz zmiany
          </d-button>
        </div>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-alert
      v-if="load_error"
      show
      theme="warning">
      Wczytywanie danych nie powiodło się. <a
        href="javascript:window.location.reload();"
      class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
    </d-alert>

    <template v-else>
      <d-card
        class="mb-5 p-4"
        style="position: relative;">
        <transition name="fade">
          <div
            v-if="!allDataLoaded"
            class="overlay-loading"
            style="z-index: 10;">
            <div class="loading-spinner pb-3 pt-3 text-center">
              <span class="loading-spinner-content text-primary">
                Trwa ładowanie danych&hellip;
              </span>
            </div>
          </div>
        </transition>

        <validation-observer
          ref="price-list-form"
          tag="form">
          <d-row class="mb-3 pb-3 border-bottom">
            <d-col
              cols="12"
              md="6">
              <div class="form-group mb-0">
                <label for="name">Nazwa cennika</label>
                <validation-provider
                  rules="required"
                  name="nazwa"
                  v-slot="{ errors }">
                  <d-input
                    id="name"
                    placeholder="Podaj nazwę cennika"
                    :class="{ 'is-invalid': errors.length }"
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>
        </validation-observer>

        <product-price-table
          ref="priceTable"
          :all-products="products"
          :loaded="loaded_products"
          :load-error="products_load_error"
          :show-load-button="isEdit"
          heading-tag="h4"
          @load-prices="showLoadPricesModal = true" />
      </d-card>
    </template>

    <load-prices-modal
      v-if="isEdit && id"
      :show-modal="showLoadPricesModal"
      :close-modal="closeLoadPricesModal"
      :entity-id="id"
      entity-type="PRICE_LIST"
      load-endpoint="/price-lists/load-prices/"
      @prices-loaded="onPricesLoaded" />
  </d-container>
</template>

<script>
import FormUtils from '../../../utils/FormUtils.js';
import LoadPricesModal from './LoadPricesModal.vue';
import ProductPriceTable from '../../common/ProductPriceTable.vue';

export default {
  name: 'price-lists-form',
  components: {
    'load-prices-modal': LoadPricesModal,
    ProductPriceTable
  },
  data () {
    return {
      id: null,
      data: {
        id: '',
        name: '',
        prices: [],
        products: []
      },
      products: [],
      loaded: false,
      loaded_products: false,
      load_error: false,
      products_load_error: false,
      form_submitting: false,
      showLoadPricesModal: false
    };
  },
  computed: {
    isEdit () {
      return !!this.$route.params.id;
    },
    allDataLoaded () {
      return this.loaded && this.loaded_products;
    }
  },
  watch: {
    'data.products': function (value) {
      if (value && value.length && this.isEdit) {
        setTimeout(() => {
          this.$refs.priceTable.setPrices(value);
        }, 500);
      }
    }
  },
  mounted () {
    this.getProducts();
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }
  },
  methods: {
    getProducts () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/products/items',
        outputKey: 'products',
        loadedKey: 'loaded_products',
        errorKey: 'products_load_error',
        params: {
          pagination: false
        }
      });
    },
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/price-lists/item/',
        instanceFields: ['id', 'name', 'prices'],
        loadedKey: 'loaded',
        errorKey: 'load_error',
        successAction: (response) => {
          if (response.prices && response.prices.length) {
            this.data.products = response.prices;
          }
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['price-list-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        name: this.data.name
      };

      this.form_submitting = true;
      let endpointUrl = this.isEdit ? '/price-lists/update/' + this.id : '/price-lists/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano cennik',
        successText: 'Cennik został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Zapisywanie cennika nie powiodło się. Spróbuj ponownie.',
        successAction: (response) => {
          let priceListId = this.isEdit ? this.id : response.item.id;
          this.submitPrices(priceListId);
        },
        failAction: () => {
          this.form_submitting = false;
        }
      });
    },
    submitPrices (priceListId) {
      let formDataToSend = this.prepareFormDataToSendPrice();
      let endpointUrl = '/price-lists/store-prices/' + priceListId;

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano ceny',
        successText: 'Ceny cennika zostały zapisane',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Zapisywanie cen nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.form_submitting = false;
          this.$router.push('/cenniki');
        },
        failAction: () => {
          this.form_submitting = false;
        }
      });
    },
    prepareFormDataToSendPrice () {
      return {
        prices: this.$refs.priceTable.getPricedProducts()
      };
    },
    closeLoadPricesModal () {
      this.showLoadPricesModal = false;
    },
    onPricesLoaded (prices) {
      this.showLoadPricesModal = false;
      this.$refs.priceTable.setPrices(prices);
    }
  }
};
</script>
